import { useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  ExternalLink,
  FileText,
  Link as LinkIcon,
  Loader2,
  Paperclip,
  Send,
  X as XIcon,
} from "lucide-react";
import { API } from "service/Interceptor";
import { applyProject } from "service/apiUrl";
import { serverFiles } from "utils/utils";
import { addCreatorBriefApplicant, isApplicationsClosed } from "utils/creatorBriefs";
import { markJobApplied } from "utils/appliedJobs";
import toast from "components/Toast/toast";

const field =
  "w-full rounded-xl bg-zinc-900/70 border border-zinc-800 text-zinc-100 placeholder:text-zinc-500 px-4 py-3 text-sm focus:outline-none focus:border-[#6C63F5]";

const ApplyDialog = ({ project, onClose, onApplied }) => {
  const [note, setNote] = useState("");
  const [workLinks, setWorkLinks] = useState("");
  const [portfolio, setPortfolio] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);

  const title = project?.jobTitle || "this brief";
  const brand =
    project?.companyJobRespDto?.name || project?.company?.name || "the brand";
  const jobId = project?.jobId;
  const postId =
    project?.postId ||
    (String(jobId || "").startsWith("post-")
      ? String(jobId).replace(/^post-/, "")
      : null);
  // Real job UUIDs (brand + new creator briefs) apply via JobApplication API.
  // Legacy post-* briefs without a job still use a comment fallback.
  const isRealJob =
    !!jobId &&
    !String(jobId).startsWith("post-") &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      String(jobId)
    );
  const hasWorkLinks = workLinks.trim().length > 0;

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const okTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    const okExt = /\.(pdf|doc|docx)$/i.test(file.name);
    if (!okTypes.includes(file.type) && !okExt) {
      toast.error("Please upload a PDF or Word document.");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("fileName", file);
      const res = await API.post("api/media", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const uploaded = res?.data?.[0];
      if (!uploaded?.path && !uploaded?.id) throw new Error("Upload failed");
      setPortfolio({
        url: uploaded.path,
        filename: file.name,
      });
      toast.success("Portfolio attached");
    } catch (er) {
      toast.error(er?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!jobId && !postId) return;
    if (isApplicationsClosed(project)) {
      setErr("Applications for this brief are closed.");
      return;
    }
    if (!workLinks.trim()) {
      setErr("Add at least one work link to send your application.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const parts = [
        "I'd like to collaborate on this brief.",
        note.trim() ? `Note: ${note.trim()}` : null,
        workLinks.trim() ? `Work: ${workLinks.trim()}` : null,
        portfolio?.url ? `Portfolio: ${portfolio.url}` : null,
      ].filter(Boolean);
      let wroteComment = false;
      let wroteJob = false;

      // Always leave a comment on the brief post so the owner can see it,
      // even when a job UUID is also present (many creator jobs have no live Job).
      if (postId) {
        const res = await API.post(`api/post/${postId}/comment`, {
          comment: parts.join("\n"),
        });
        if (!res?.data?.success) {
          throw new Error(res?.data?.message || "Could not apply");
        }
        wroteComment = true;
        addCreatorBriefApplicant(jobId || `post-${postId}`, {
          note: note.trim() || "Applied to your brief",
          workLinks: workLinks.trim() || null,
          portfolio: portfolio?.url || null,
          status: "pending",
        });
      }

      if (isRealJob) {
        try {
          const res = await API.post(`${applyProject}?jobId=${jobId}`, null, {
            skipErrorToast: !!postId,
          });
          wroteJob = !!res?.data?.success;
          if (!wroteJob && !wroteComment) {
            throw new Error(res?.data?.message || "Could not apply");
          }
        } catch (jobErr) {
          if (!wroteComment) throw jobErr;
        }
      }

      if (!wroteComment && !wroteJob) {
        throw new Error("Could not apply");
      }

      markJobApplied(jobId, postId ? `post-${postId}` : null);
      toast.success("Application sent");
      onApplied?.();
      onClose();
    } catch (er) {
      setErr(
        er?.response?.data?.message || er?.message || "Could not apply"
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] flex items-center justify-center p-4"
    >
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <motion.form
        onSubmit={submit}
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 20, opacity: 0 }}
        className="relative w-full max-w-lg rounded-2xl border border-zinc-800 bg-zinc-950 p-6 max-h-[92vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-[0.16em] text-[#8b83ff] font-semibold">
              Apply
            </div>
            <h3 className="mt-1 font-display font-bold text-xl text-zinc-50 line-clamp-2">
              {title}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-zinc-500 hover:text-zinc-200"
          >
            <XIcon size={20} />
          </button>
        </div>
        <p className="text-xs text-zinc-500 mb-4">
          Once you apply, {brand} can see your email to reach out.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm text-zinc-300 mb-2 font-medium">
              Note
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="Short note about why you're a fit (optional)"
              className={`${field} resize-none`}
            />
          </div>

          <div>
            <label className="block text-sm text-zinc-300 mb-2 font-medium">
              Work links <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <LinkIcon
                size={14}
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500"
              />
              <input
                value={workLinks}
                onChange={(e) => {
                  setWorkLinks(e.target.value);
                  if (err) setErr("");
                }}
                required
                aria-required="true"
                placeholder="Instagram, Vimeo, website — paste one or more"
                className={`${field} pl-9`}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-zinc-300 mb-2 font-medium">
              Portfolio{" "}
              <span className="text-zinc-500 font-normal">
                (PDF or Word — optional)
              </span>
            </label>
            <input
              ref={fileRef}
              type="file"
              hidden
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={onPickFile}
            />
            {portfolio ? (
              <div className="flex items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-900/70 px-4 py-3">
                <div className="h-9 w-9 rounded-lg bg-[#6C63F5]/15 flex items-center justify-center text-[#8b83ff]">
                  <FileText size={16} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-zinc-100 truncate">
                    {portfolio.filename}
                  </div>
                  {portfolio.url && (
                    <a
                      href={serverFiles(portfolio.url)}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-[#8b83ff] hover:text-white inline-flex items-center gap-1"
                    >
                      Preview <ExternalLink size={11} />
                    </a>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setPortfolio(null)}
                  className="text-zinc-500 hover:text-red-300 h-8 w-8 rounded-full inline-flex items-center justify-center"
                >
                  <XIcon size={15} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="w-full rounded-xl border border-dashed border-zinc-700 hover:border-[#6C63F5]/60 bg-zinc-900/40 px-4 py-3 text-sm text-zinc-300 inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
              >
                {uploading ? (
                  <>
                    <Loader2 size={14} className="animate-spin" /> Uploading…
                  </>
                ) : (
                  <>
                    <Paperclip size={14} className="text-[#8b83ff]" /> Attach
                    portfolio
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {err && (
          <div className="mt-4 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-300">
            {err}
          </div>
        )}
        <button
          type="submit"
          disabled={busy || uploading || !hasWorkLinks}
          className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#6C63F5] text-white hover:bg-[#5751E5] rounded-full py-3 text-sm font-semibold disabled:opacity-50"
        >
          {busy ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Send size={14} />
          )}{" "}
          Send application
        </button>
      </motion.form>
    </motion.div>
  );
};

export default ApplyDialog;
